import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CIMA } from "@/lib/cima";

const NAV_LINKS = [
  { href: "#sobre", label: "Sobre Nosotros" },
  { href: "#servicios", label: "Catálogo", isCatalog: true },
  { href: "#productos", label: "Productos" },
  { href: "#seguridad", label: "Seguridad" },
  { href: "#testimonios", label: "Testimonios" },
  { href: "#contacto", label: "Contacto" },
];

export default function Navbar({ onOpenQuote }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => {
    setOpen(false);
    if (!onHome) {
      // Navigate home and then to anchor
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (onHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="cima-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "glass-nav" : "bg-white/95 border-b border-slate-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="#top"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            onClick={handleLogoClick}
          >
            <img
              src={CIMA.logoUrl}
              alt="CIMA - Comercializadora Industrial Médica"
              className="h-12 lg:h-14 w-auto object-contain"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              l.isCatalog ? (
                <Link
                  key={l.href}
                  to="/catalogo"
                  data-testid="nav-link-catalogo"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-slate-700 hover:text-[var(--cima-red)] transition-colors relative group"
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[var(--cima-orange)] group-hover:w-full transition-all" />
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.href.replace("#", "")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(l.href);
                  }}
                  className="text-sm font-medium text-slate-700 hover:text-[var(--cima-red)] transition-colors relative group"
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[var(--cima-orange)] group-hover:w-full transition-all" />
                </a>
              )
            )}
          </nav>

          {/* Right CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              data-testid="nav-phone"
              href={`tel:${CIMA.phoneTel}`}
              className="flex items-center gap-2 text-sm font-medium text-slate-900 hover:text-[var(--cima-red)]"
            >
              <Phone className="w-4 h-4" />
              {CIMA.phone}
            </a>
            <button
              data-testid="nav-quote-button"
              onClick={onOpenQuote}
              className="btn-cima-red px-5 py-2.5 text-sm font-semibold uppercase tracking-wide"
            >
              Solicitar Cotización
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded border border-slate-200"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-slate-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) =>
              l.isCatalog ? (
                <Link
                  key={l.href}
                  to="/catalogo"
                  data-testid="nav-mobile-link-catalogo"
                  onClick={() => setOpen(false)}
                  className="px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick(l.href);
                  }}
                  className="px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded"
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href={`tel:${CIMA.phoneTel}`}
              data-testid="nav-mobile-phone"
              className="px-3 py-3 text-base font-medium text-slate-800 flex items-center gap-2 border-t border-slate-100 mt-2"
            >
              <Phone className="w-4 h-4" /> {CIMA.phone}
            </a>
            <button
              data-testid="nav-mobile-quote-button"
              onClick={() => {
                setOpen(false);
                onOpenQuote();
              }}
              className="btn-cima-red mt-2 w-full py-3 text-sm font-semibold uppercase tracking-wide"
            >
              Solicitar Cotización
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
